import React, { memo } from "react";
import { CalendarDays } from "lucide-react";
import { shortDate } from "./fmt";

/*
 * EXPIRY / EVENT STRIP — the calendar facts a buyer must know before the
 * first click: which expiry, how many days to it, whether today IS the expiry
 * (and whether we are past the 13:30 no-new-buys tail), holiday / weekend
 * state, a user-maintained event note, and the sizing hint that follows.
 * Pure presentation over fetchBuyerEdge().calendar.
 */

/* The next scheduled events after today (RBI / US Fed / US CPI from the published
 * calendars, plus the user's own days) — /dashboard/events. Today's event is
 * already the `Event:` chip, so it is skipped here. */
function UpcomingEvents({ events, today }) {
  const next = (events || []).filter((e) => e.date > today).slice(0, 2);
  if (!next.length) return null;
  return next.map((e) => (
    <span key={`${e.date}-${e.label}`} title={e.source_date && e.source_date !== e.date
      ? `${e.label} — published for ${e.source_date}; this is the Indian session it can move` : e.label}
      className={`rounded-lg border px-2 py-1 font-semibold ${e.importance >= 3
        ? "border-amber-500/30 bg-amber-500/[.06] text-amber-200" : "border-line bg-surface-2 text-slate-300"}`}>
      {shortDate(e.date)}: {e.label}
    </span>
  ));
}

function ExpiryStrip({ calendar, events }) {
  if (!calendar) return null;
  const c = calendar;
  const dteText = c.dte == null ? "" : ` · ${c.dte} DTE`;
  const closed = c.trading_day === false;
  const sizingTone = /half|quarter|no /i.test(String(c.sizing_hint || "")) ? "text-amber-300" : "text-slate-400";
  return (
    <div data-testid="expiry-strip" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
      <span className="inline-flex items-center gap-1 rounded-lg border border-line bg-surface-2 px-2 py-1 font-bold text-slate-300">
        <CalendarDays className="h-3 w-3 text-slate-500" />
        {c.expiry ? `Expiry ${shortDate(c.expiry)}${dteText}` : "Expiry —"}
      </span>
      {c.expiry_day && (
        <span className="rounded-lg border border-rose-500/40 bg-rose-500/[.12] px-2 py-1 font-black uppercase tracking-wider text-rose-300">
          Expiry day
        </span>
      )}
      {c.expiry_tail && (
        <span className="rounded-lg border border-rose-500/40 bg-rose-500/[.08] px-2 py-1 font-bold text-rose-300">
          after 13:30: no new buys
        </span>
      )}
      {closed && (
        <span className="rounded-lg border border-slate-700 bg-slate-900/60 px-2 py-1 font-bold uppercase tracking-wider text-slate-400">
          {c.holiday ? "Market holiday" : "Weekend"}
        </span>
      )}
      {c.event && (
        <span className="rounded-lg border border-amber-500/30 bg-amber-500/[.08] px-2 py-1 font-bold text-amber-300" title="Published RBI / Fed / CPI calendar + Settings → calendar_event_days">
          Today: {c.event}
        </span>
      )}
      {c.sizing_hint && <span className={`font-bold ${sizingTone}`}>{c.sizing_hint}</span>}
      <UpcomingEvents events={events} today={c.date} />
    </div>
  );
}

export default memo(ExpiryStrip);
